import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Eye, Loader2, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { ISSUES } from "@/data/issues";
import VoiceCard from "@/components/VoiceCard";
import ShareButtons from "@/components/ShareButtons";
import SiteFooter from "@/components/SiteFooter";
import "@/voteforme.css";

const LOGO_WHITE = "/sbgwc-logo-white.png";

export default function Champion() {
  const { slug } = useParams();
  const [pledge, setPledge] = useState(undefined); // undefined loading, null not found

  useEffect(() => {
    api.get(`/pledges/${slug}`)
      .then(({ data }) => {
        setPledge(data);
        if (data.slug !== slug) window.history.replaceState(null, "", `/p/${data.slug}`);
      })
      .catch(() => setPledge(null));
  }, [slug]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied ✓");
  };

  if (pledge === undefined) {
    return (
      <div className="vfm"><div className="vfm-bg-glow" />
        <div className="champion-page" style={{ justifyContent: "center" }}>
          <Loader2 className="spin" size={30} style={{ animation: "spin 1s linear infinite", color: "var(--dream-aqua)" }} />
        </div>
      </div>
    );
  }

  if (pledge === null) {
    return (
      <div className="vfm"><div className="vfm-bg-glow" />
        <div className="champion-page" style={{ justifyContent: "center" }}>
          <img className="champion-logo" src={LOGO_WHITE} alt="SBGWC" />
          <h1 className="champion-heading">Champion not found</h1>
          <p className="champion-sub">This link doesn't match any voice card yet.</p>
          <Link to="/" className="btn-primary" style={{ textDecoration: "none" }}>Create your own →</Link>
        </div>
      </div>
    );
  }

  const facts = pledge.issues.map((id) => ISSUES.find((x) => x.id === id)).filter(Boolean);
  const shareText = `${pledge.name} can't vote yet — but ${pledge.issues.join(", ").toLowerCase()} shape their future. If you can vote, please vote for ${pledge.name}, until they can. #VoteForMeUntilICan`;
  const getImage = pledge.card_url
    ? async () => {
        const blob = await (await fetch(`${process.env.REACT_APP_BACKEND_URL}${pledge.card_url}`)).blob();
        return await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(blob); });
      }
    : null;

  return (
    <div className="vfm"><div className="vfm-bg-glow" />
      <div className="champion-page">
        <img className="champion-logo" src={LOGO_WHITE} alt="SBGWC" />
        <div className="champion-eyebrow">#VoteForMeUntilICan</div>
        <h1 className="champion-heading">{pledge.name} can't vote yet — but has a voice.</h1>
        <p className="champion-sub">If you <b>can</b> vote, {pledge.name} is asking you to vote for them and the issues that shape their future.</p>

        <div className="champion-card-wrap">
          <VoiceCard name={pledge.name} age={pledge.age} issues={pledge.issues} custom={pledge.custom_message} city={pledge.city} state={pledge.state} />
        </div>

        <div className="champion-views"><Eye size={15} /> Seen <b data-testid="champion-views">{pledge.views}</b> {pledge.views === 1 ? "time" : "times"}</div>

        <div style={{ width: "100%", maxWidth: 480, margin: "6px auto 0" }}>
          <div className="cta-msg-label" style={{ marginBottom: 8 }}>Pass {pledge.name}'s voice along</div>
          <ShareButtons text={shareText} url={window.location.href} getImage={getImage} name={pledge.name} />
        </div>

        {facts.length > 0 && (
          <div className="champion-facts">
            {facts.map((d) => (
              <div className="fact-card" key={d.id} style={{ opacity: 1, animation: "none" }}>
                <div className="fact-label"><span className="fact-emoji">{d.emoji}</span><span className="fact-issue">{d.name}</span></div>
                <p className="fact-text">{d.fact}</p>
                <p className="fact-why">Why voting matters: {d.why}</p>
              </div>
            ))}
          </div>
        )}

        <div className="champion-actions">
          <a className="btn-yellow" href="https://blackvotersmatterfund.org/vote/" target="_blank" rel="noopener noreferrer" style={{ textAlign: "center", textDecoration: "none" }} data-testid="voter-toolbox-btn">🗳️ Open the Voter Toolbox</a>
          <button className="btn-secondary" onClick={copyLink} data-testid="copy-link-btn"><Copy size={14} style={{ marginRight: 6, verticalAlign: "-2px" }} />Share this page</button>
          <Link to="/" className="btn-secondary" style={{ textAlign: "center", textDecoration: "none" }} data-testid="make-own-btn">Make your own voice card →</Link>
        </div>
        <Link to={`/edit?slug=${pledge.slug}`} className="edit-link" data-testid="edit-pledge-link"><Pencil size={12} /> Is this your card? Edit your pledge</Link>
      </div>
      <SiteFooter />
    </div>
  );
}
